import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, MapPin, Clock, Image, Video, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const upcomingEvents = [
  {
    id: '1',
    title: 'UIEHEC Global Education Summit 2025',
    description: 'Join educators and administrators from around the world for our flagship annual event featuring keynote speakers, workshops, and networking opportunities.',
    date: '2025-06-15',
    location: 'Aachen, Germany',
    type: 'upcoming',
    image_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800'
  },
  {
    id: '2',
    title: 'Innovation in Higher Education Forum',
    description: 'A forum dedicated to exploring innovative approaches in higher education, technology integration, and future trends.',
    date: '2025-09-10',
    location: 'Virtual Event',
    type: 'upcoming',
    image_url: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=800'
  },
];

const pastEvents = [
  {
    id: '3',
    title: 'Founding Members Ceremony and MoU Signing Event',
    description: 'Official ceremony celebrating our founding members and the signing of the Memorandum of Understanding.',
    date: '2025-04-20',
    location: 'Aachen, Germany',
    type: 'past'
  },
  {
    id: '4',
    title: 'International Education Roundtable 2024',
    description: 'A roundtable discussion on international education trends and challenges.',
    date: '2024-11-15',
    location: 'Istanbul, Turkey',
    type: 'past'
  },
  {
    id: '5',
    title: 'Global Power Union Partnership Forum',
    description: 'Partnership forum with Global Power Union discussing collaborative opportunities.',
    date: '2024-09-22',
    location: 'Warsaw, Poland',
    type: 'past'
  },
];

export default function EventsPage() {
  const [events, setEvents] = useState([...upcomingEvents, ...pastEvents]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        if (response.data.length > 0) {
          setEvents([...response.data, ...pastEvents]);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const upcoming = events.filter(e => e.type === 'upcoming' || new Date(e.date) >= new Date());
  const past = events.filter(e => e.type === 'past' || new Date(e.date) < new Date());

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0E285B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Calendar className="h-6 w-6 text-[#F5A623]" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Events & Conferences
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Events and Conferences
            </h1>
            <p className="text-slate-300">
              Join us at our conferences, workshops, and networking events 
              to connect with the global education community.
            </p>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B] mb-8">
            Upcoming Events
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {upcomingEvents.map((event, index) => (
              <div 
                key={event.id} 
                className="bg-white rounded-sm overflow-hidden shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                data-testid={`upcoming-event-${index}`}
              >
                <div className="h-48 overflow-hidden bg-slate-100">
                  {event.image_url ? (
                    <img 
                      src={event.image_url} 
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Calendar className="h-16 w-16 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B] mb-3">
                    {event.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    {event.description}
                  </p>
                  <div className="space-y-2 text-sm text-slate-500">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#F5A623]" />
                      {formatDate(event.date)}
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#F5A623]" />
                      {event.location}
                    </div>
                  </div>
                  <Button 
                    className="w-full mt-4 bg-[#F5A623] text-[#0E285B] hover:bg-[#F5A623]/90"
                  >
                    Register for Event
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="outline" className="gap-2 border-[#0E285B] text-[#0E285B]">
              <Users className="h-4 w-4" />
              Register for Upcoming Events
            </Button>
            <Button variant="outline" className="gap-2 border-[#0E285B] text-[#0E285B]">
              <Image className="h-4 w-4" />
              Photo Gallery
            </Button>
            <Button variant="outline" className="gap-2 border-[#0E285B] text-[#0E285B]">
              <Video className="h-4 w-4" />
              Video Highlights
            </Button>
          </div>
        </div>
      </section>

      {/* Past Events */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B] mb-8">
            Past Events Archive
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {pastEvents.map((event, index) => (
              <div 
                key={event.id}
                className="bg-slate-50 p-6 rounded-sm"
                data-testid={`past-event-${index}`}
              >
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#0E285B] mb-2">
                  {event.title}
                </h3>
                <p className="text-slate-600 text-sm mb-3">
                  {event.description}
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#F5A623]" />
                    {formatDate(event.date)}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#F5A623]" />
                    {event.location}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
